import { useEffect, useState } from "react";
import axios from "axios";
import { getOrganizations } from "../../api/organization.api";
import { usePassword } from "../../hooks/use-password.hook";
import type { Organization } from "../../types/organization.type";

type OrganizationColumnKey =
  | "name"
  | "displayName"
  | "mcNumber"
  | "lhNumberPrefix"
  | "hrEmail"
  | "organizationId"
  | "inviteToken"
  | "tier"
  | "loadsForwardingEmail"
  | "createdAt";

const COLUMNS: { key: OrganizationColumnKey; label: string }[] = [
  { key: "name", label: "Name" },
  { key: "displayName", label: "Display Name" },
  { key: "mcNumber", label: "MC Number" },
  { key: "lhNumberPrefix", label: "LH Number Prefix" },
  { key: "hrEmail", label: "HR Email" },
  { key: "organizationId", label: "Organization ID" },
  { key: "inviteToken", label: "Invite Token" },
  { key: "tier", label: "Tier" },
  { key: "loadsForwardingEmail", label: "Loads Forwarding Email" },
  { key: "createdAt", label: "Created At" },
];

function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { message?: string; error?: string }
      | undefined;
    return data?.message ?? data?.error ?? error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Something went wrong.";
}

function formatCell(value: Organization[OrganizationColumnKey]) {
  if (value == null || value === "") {
    return "—";
  }
  if (value instanceof Date || (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value))) {
    const date = value instanceof Date ? value : new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleString();
    }
  }
  return String(value);
}

export function ViewOrganizations() {
  const password = usePassword();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();

    setIsLoading(true);
    setError("");
    getOrganizations(password, { signal: controller.signal })
      .then((data) => {
        setOrganizations(data.organizations);
      })
      .catch((loadError) => {
        if (axios.isCancel(loadError) || controller.signal.aborted) {
          return;
        }
        setError(getErrorMessage(loadError));
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [password]);

  if (isLoading) {
    return (
      <div className="org-view-loading" role="status" aria-live="polite">
        <span className="org-spinner" aria-hidden="true" />
        <span>Loading organizations…</span>
      </div>
    );
  }

  if (error) {
    return (
      <p className="org-form-error" role="alert">
        Failed to load organizations: {error}
      </p>
    );
  }

  if (organizations.length === 0) {
    return <p className="org-view-empty">No organizations found.</p>;
  }

  return (
    <div className="org-view-table-wrapper">
      <table className="org-view-table">
        <thead>
          <tr>
            {COLUMNS.map((column) => (
              <th key={column.key}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {organizations.map((organization) => (
            <tr key={organization.organizationId}>
              {COLUMNS.map((column) => (
                <td key={column.key}>{formatCell(organization[column.key])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
